import { Head, router, useForm } from "@inertiajs/react"
import { Link } from "@inertiajs/react"
import { ArrowLeft, MessageCircle, Send } from "lucide-react"

import ContentActionMenu from "@/components/content-action-menu"
import PostCard from "@/components/feed/post-card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import { formatRelativeTime } from "@/lib/time"
import type { PostsCommentsIndex as PostsCommentsIndexProps } from "@/types/serializers"
import type RequestsCreateComment from "@/types/serializers/RequestsCreateComment"

export default function CommentsIndex({
  post,
  comments,
  pagination,
}: PostsCommentsIndexProps) {
  const form = useForm<RequestsCreateComment>({ comment: { body: "" } })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post(`/publicaciones/${post.id}/comentarios`, {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    })
  }

  function goToPage(page: number) {
    router.visit(`/posts/${post.id}/comments?page=${page}`)
  }

  return (
    <AppLayout>
      <Head title="Comentarios | KrownFT" />
      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-2xl">
          {/* Header */}
          <div className="border-border flex items-center gap-3 border-b px-4 py-3">
            <Link
              href="/inicio"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="font-semibold">Publicación</h1>
          </div>

          {/* Post */}
          <PostCard post={post} />

          {/* Comment input */}
          <div className="border-border border-b px-4 py-3">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                placeholder="Escribí un comentario..."
                value={form.data.comment.body}
                onChange={(e) =>
                  form.setData("comment", {
                    ...form.data.comment,
                    body: e.target.value,
                  })
                }
                className="flex-1 text-sm"
              />
              <Button
                type="submit"
                size="sm"
                disabled={form.processing || !form.data.comment.body.trim()}
                className="bg-krown-200 hover:bg-krown-100 text-white"
              >
                <Send className="mr-1 h-3 w-3" />
                Enviar
              </Button>
            </form>
          </div>

          {/* Comments list */}
          <div className="px-4 py-4">
            {comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={comment.user.avatar_url ?? undefined} />
                      <AvatarFallback className="bg-krown-200 text-xs text-white">
                        {comment.user.first_name[0]}
                        {comment.user.last_name[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-semibold">
                          {comment.user.full_name}
                        </span>{" "}
                        <span className="text-muted-foreground">
                          {comment.body}
                        </span>
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {formatRelativeTime(comment.created_at)}
                      </p>
                    </div>
                    <ContentActionMenu
                      reportableType="Comment"
                      reportableId={comment.id}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <MessageCircle className="text-muted-foreground/50 mx-auto mb-3 h-10 w-10" />
                <p className="text-muted-foreground text-sm">
                  Aún no hay comentarios
                </p>
              </div>
            )}

            {pagination.pages > 1 && (
              <div className="mt-6 flex justify-center gap-2">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
                  (page) => (
                    <Button
                      key={page}
                      variant={page === pagination.page ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </Button>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
